import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { normalizeProduct, storeTabsFromSections } from '../lib/format';

const API = (process.env.REACT_APP_API_URL || 'https://nrkmart-api.vercel.app').replace(/\/$/, '');
const STORE_KEY = 'nrk_active_store';
const DELIVER_KEY = 'nrk_deliver_to';

function readSavedStore() {
  try { return localStorage.getItem(STORE_KEY) || 'groceries'; } catch { return 'groceries'; }
}
function readDeliverTo() {
  try { const raw = localStorage.getItem(DELIVER_KEY); if (raw) return JSON.parse(raw); } catch { /* ignore */ }
  return null;
}

const CatalogContext = createContext(null);

/**
 * Product catalog + delivery-location state shared by Home, Categories, and
 * the shell's desktop navbar (search placeholder / deliver-to chip). Ported
 * from OrderPage.js's on-mount fetch + store-tab logic, unchanged.
 */
export function CatalogProvider({ lat, lng, children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeSection, setActiveSectionState] = useState(() => readSavedStore());
  const [deliverTo, setDeliverTo] = useState(() => readDeliverTo());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!API) { setLoadError('REACT_APP_API_URL is not set.'); setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/api/products`);
        if (cancelled) return;
        const units = res.data.units || {};
        setProducts((res.data.products || []).map((p) => normalizeProduct(p, units)));
        setCategories(res.data.categories || []);
        setSections(res.data.sections || []);
      } catch (err) {
        if (!cancelled) setLoadError(`Could not reach backend: ${err.response?.data?.error || err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (deliverTo) return;
    if (lat && lng) {
      const label = `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
      const next = { lat: Number(lat), lng: Number(lng), label };
      setDeliverTo(next);
      try { localStorage.setItem(DELIVER_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    }
  }, [lat, lng, deliverTo]);

  const storeTabs = useMemo(() => storeTabsFromSections(sections), [sections]);

  useEffect(() => {
    if (!storeTabs.length) return;
    if (!storeTabs.some((s) => s.id === activeSection)) {
      const next = storeTabs.find((s) => s.id === 'groceries')?.id || storeTabs[0].id;
      setActiveSectionState(next);
      try { localStorage.setItem(STORE_KEY, next); } catch { /* ignore */ }
    }
  }, [storeTabs, activeSection]);

  const selectStore = (id) => {
    setActiveSectionState(id);
    try { localStorage.setItem(STORE_KEY, id); } catch { /* ignore */ }
  };

  const refreshDeliverTo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const la = pos.coords.latitude;
        const lo = pos.coords.longitude;
        let label = `${la.toFixed(4)}, ${lo.toFixed(4)}`;
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${la}&lon=${lo}&format=json`, { headers: { 'Accept-Language': 'en' } });
          const d = await r.json();
          if (d.display_name) label = d.display_name.split(',').slice(0, 3).join(', ');
        } catch { /* ignore */ }
        const next = { lat: la, lng: lo, label };
        setDeliverTo(next);
        try { localStorage.setItem(DELIVER_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      },
      () => {},
      { timeout: 10000, enableHighAccuracy: true },
    );
  };

  const retryLoad = () => window.location.reload();

  const value = {
    products, categories, sections, loading, loadError,
    activeSection, selectStore, storeTabs,
    deliverTo, refreshDeliverTo, retryLoad,
  };

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog must be inside CatalogProvider');
  return ctx;
}
